import React, { useEffect } from 'react';
import WtBalRow from './WbalRow';
import WtBalGraph from './WtBalGraph';
import { API_BASE } from '../apiBase';
import { useNavigate } from "react-router-dom";

console.log("WbalTable: LOADED components/WbalTable.js (MARKER A)");

// order for aircraft Normal-category performance operating stations in the POH or AFM.
// the Utility-category is not referenced here
const STATION_ORDER = [
  "Basic Empty Weight",
  "Pilot",
  "CoPilot",
  "RearPass1",
  "RearPass2",
  "Baggage",
];

/**
 * psudo-code for weight and balance table:
 * 1. sort stations in predefined order, then fuel rows, then final sum row
 * 2. normal stations are displayed as read-only rows
 * 3. fuel rows: initial fuel is editable, runup fuel is editable, takeoff fuel is computed
 * 4. useEffect to fetch wbal data for the given tail number on component mount
 * 5. compute fuel weights and moments based on input gallons and constants from fuel object
 * 6. compute sum total weight, moment, and CG at takeoff
 * 
 * 
 */

/** 
 * 
 * helper function to extract a station key for sorting purposes
 * stationKey:anonymous function that takes one parameter 'w'
 * 'w' is a row object that may have properties like 'id', 'station', or 'name'
 * w?.id is for optional chaining. if 'w' is null or undefined, it won't throw an error
 * 
 * ?? nullish coalescing operator. if w?.id is null or undefined, it will check w?.station
 * fallback is empty string if all are null or undefined
 */
const stationKey = (w) => String(w?.id ?? w?.station ?? w?.name ?? "").trim();  

/**
 * anonymous function that takes an array of row objects
 * uses map method to create a new map object with 'k' key and 'i' index
 * sorts the array based on the index of the station key in the predefined order
 * it references the STATION_ORDER constant to determine the order of stations
 * it turns the station key to lowercase and combines it with the index of priority
 * the return statement shallow copies (no mutation) the original array
 * const ak bk gets clean lowercase names for the row
 * ai bi gets priority index for the station
 * if station exists it uses the index, otherwise id assigns 999 to push it to the end
 * if two stations have the same priority, it falls back to alphabetical order
 */
const sortStations = (arr) => {
  const order = new Map(STATION_ORDER.map((k, i) => [k.toLowerCase(), i]));
  return [...arr].sort((a, b) => {
    const ak = stationKey(a).toLowerCase();
    const bk = stationKey(b).toLowerCase();
    const ai = order.has(ak) ? order.get(ak) : 999;
    const bi = order.has(bk) ? order.get(bk) : 999;
    if (ai !== bi) return ai - bi;
    return ak.localeCompare(bk);
  });
};
/**
 * anonymous function that takes one parameter 'w'
 * 'w' is a row object that may have properties like 'id', 'station', or 'name'
 * w?.id is for optional chaining. if 'w' is null or undefined, it won't throw an error
 * ? ternary operator is used to safely access the id without throwing an error if 'w' is null
 * ?? nullish coalescing operator. if w?.id is null or undefined
 *       fallback is empty string if null or undefined
 * it checks if the station key is equal to "limits" (case-insensitive)
 * returns true if it is a limits row, false otherwise
 */
const isLimits = (w) => String(w?.id ?? "").toLowerCase() === "limits";

// helper function to identify if a row is a fuel object based on its properties
// returns true if row is a fuel object, false otherwise
const isFuelObj = (w) => {
  const id = String(w?.id ?? "").toLowerCase();
  return (
    id === "fuel" ||
    w?.lbsPerGal != null ||
    w?.initialFuelGal != null ||
    w?.runupFuelGal != null ||
    w?.usableGals != null ||
    w?.type != null
  );
};
/**
 * ---------------------------------------------------
 * helper function used to convert strings to numbers
 * this would be used during a save operation
 * 'wbal' data is stored as numeric data
 * ternary operator is used to treat empty strings as null values
 * Number() function is used to convert the string to a number
 * Number.isFinite() is used to check if the result is a valid finite number
 * if it is a valid number, it returns the number, otherwise it returns null
 * ---------------------------------------------------
 */
const toNum = (v) => {
   if (v === "" || v == null) return null;  // treat empty string as null
  const n = Number(v);
  return Number.isFinite(n) ? n : null;
};

function WtBalTable({ tail, onEdit, onDelete, canEdit = false }) {
  const [wbal, setWtbals] = React.useState([]);
  const [envelope, setEnvelope] = React.useState([]);
  const [initialFuelGal, setInitialFuelGal] = React.useState(""); // editable
  const [runupFuelGal, setRunupFuelGal] = React.useState("");
  const [fuelArm,setFuelArm] = React.useState("");
  const [maxGrossLbs, setMaxGrossLbs] = React.useState("");
  const [unusableGals, setUnusableFuelGal] = React.useState("");
  
  
  const navigate = useNavigate();

  console.log("WbalTable: WtBalTable function LOADED");

  const handleDelete = async (id) => {
    const ok = await onDelete?.(id);
    if (ok) {
      setWtbals((prev) =>
        prev.filter((r) => String(r.id) !== String(id))
      );
    }
  };

  /**
   * useEffect mounts the component and fetches wbal data for the given tail number
   * it defines an asynchronous function fetchWbal to perform the fetch operation
   * the URL is constructed using the API_BASE constant and the tail number
   *  as a query parameter. 
   * it uses the Document Object Model (DOM) lifecycle for React components.
   * When fetchWbal executes, the state hooks are used to update the component state,
   * which triggers a re-render of the component with the new data.
   * DOM elements are updated based on the new state.  
   * Note: the DOM tree is created in the browser using the HTML 
   * returned JSX from the component, 
   * and React manages the updates to the DOM efficiently when state changes occur.
   * 
   * Arrays and objects may be stored in the heap memory
   * when state is updated with new arrays or objects.
   * 
   */
  useEffect(() => {
    if (!tail) return;
    
    const fetchWbal = async () => {
      try {
        const url = `${API_BASE}/api/wtbals?tail=${encodeURIComponent(tail)}`;
        const res = await fetch(url);

        const text = await res.text();
        console.log("WbalTable: W&B fetch url:", url);
        console.log("WbalTable: W&B status:", res.status);
        console.log("WbalTable: W&B raw:", text.slice(0, 200));

        if (!res.ok) throw new Error(`HTTP ${res.status}: ${text.slice(0, 120)}`);

        // parse the data and extract the limits row for the envelope
        // if data is an array, use it, otherwise look for a 'wtbals' property
        const data = JSON.parse(text);
        const list = Array.isArray(data) ? data : (data.wtbals || []);

        // using the list array or an empty array
        // find the limits row where id is "limits" (case-insensitive)
        const limitsRow =
          (list || []).find((r) => String(r?.id ?? "").toLowerCase() === "limits") || null;

        if (limitsRow?.maxGrossLbs != null) {
            setMaxGrossLbs(String(limitsRow.maxGrossLbs));
          }

        const env = Array.isArray(limitsRow?.forwardEnvelope)
          ? limitsRow.forwardEnvelope
          : [];

        console.log("WbalTable: limitsRow =", limitsRow);
        console.log("WbalTable: envelope from firestore =", env);
        console.log("WbalTable >>> limitsRow:", limitsRow);
        console.log("WbalTable >>> envelope extracted from Firestore:", env);
        console.log("WbalTable >>> envelope length:", env.length);

        setEnvelope(env);

        // remove "limits"
        const cleaned = (list || []).filter((r) => !isLimits(r));

        setWtbals(cleaned);

        // seed initial fuel input from firestore fuel object
        const fuel = cleaned.find(isFuelObj);

        console.log("Fuel object:", fuel);
        console.log("Cleaned W&B rows:", cleaned);


        if (fuel) {
          if (fuel.initialFuelGal != null) {
            setInitialFuelGal(String(fuel.initialFuelGal));
          }
          if (fuel.runupFuelGal != null) {
            setRunupFuelGal(String(fuel.runupFuelGal));
          }
          if (fuel.unusableGals != null) {
                setUnusableFuelGal(String(fuel.unusableGals));
              }
          if (fuel.Arm != null) {
            setFuelArm(String(fuel.Arm));
          }
        }
      } catch (error) {
        console.error(error);
        setWtbals([]);
      }
    };
    // call the async fetch function to load data
    // this will trigger a re-render with the fetched data
    fetchWbal();
  }, [tail]);

  // make object that is only related to fuel.
  const fuelObj = wbal.find(isFuelObj) || null;

  console.log("fuelObj after state update:", fuelObj);

  // filter out normal stations (exclude fuel objects and limits)
  const normalRowsRaw = wbal.filter((r) => {
  const id = String(r?.id ?? "").toLowerCase().trim();

  return (
    !isFuelObj(r) &&
    id !== "initial fuel" &&
    id !== "runup fuel" &&
    id !== "takeoff fuel"
  );
});
  const normalRows = sortStations(normalRowsRaw);

  // Pull fuel constants from fuelObj (arm is pulled from firebase)
  // if fuelObj is not null, then pull the constants
  // set to a number value.  Use coalescing operator to default to 6.1
  // if fuelObj or lbsPerGal is null or undefined, default to 6.1


  const storedLbsPerGal = toNum(fuelObj?.lbsPerGal);

  const lbsPerGal =
    storedLbsPerGal != null && storedLbsPerGal > 0
      ? storedLbsPerGal
      : 6.1;
      
  const fuelArmNum = toNum(fuelArm);
  const runupGal = toNum(runupFuelGal);

  // Editable initial fuel gallons
  const initGalNum = toNum(initialFuelGal);

  // Compute fuel weights & moments
  // if initial fuel gallons is not null and lbs per gallon is not null, 
  // then compute initial fuel weight, otherwise null
  const initialFuelLb =
    initGalNum != null && lbsPerGal != null ? initGalNum * lbsPerGal : null;

  // if fuel weight is not null and fuel arm is not null, then compute moment, otherwise null
  const initialFuelMoment =
    initialFuelLb != null && fuelArm != null ? initialFuelLb * fuelArm : null;

    // if runup fuel gallons is not null and lbs per gallon is not null, 
    // then compute runup fuel weight, otherwise null
  const runupFuelLb =
    runupGal != null && lbsPerGal != null ? runupGal * lbsPerGal : null;

    // if runup fuel moment is not null and fuel arm is not null
    // then compute runup fuel moment, otherwise null
  const runupFuelMoment =
    runupFuelLb != null && fuelArm != null ? runupFuelLb * fuelArm : null;

  const takeoffGal =
    initGalNum != null && runupGal != null ? Math.max(0, initGalNum - runupGal) : null;

  const takeoffFuelLb =
    takeoffGal != null && lbsPerGal != null ? takeoffGal * lbsPerGal : null;

  const unusableGalNum = toNum(unusableGals);

  const takeoffUsableGal =
    takeoffGal != null && unusableGalNum != null
      ? Math.max(0, takeoffGal - unusableGalNum)
      : null;
 
  const takeoffFuelMoment =
    takeoffFuelLb != null && fuelArm != null ? takeoffFuelLb * fuelArm : null;


  // Sum Total = sum of normal station weights/moments + takeoff fuel weight/moment
  const sumNormalWeight = normalRows.reduce((acc, r) => acc + (toNum(r?.Weight) ?? 0), 0);
  const sumNormalMoment = normalRows.reduce((acc, r) => acc + (toNum(r?.momentLbIn) ?? 0), 0);

  const sumTotalWeight = sumNormalWeight + (takeoffFuelLb ?? 0);
  const sumTotalMoment = sumNormalMoment + (takeoffFuelMoment ?? 0);
  const sumTotalCg = 
    sumTotalWeight !== 0 ? (sumTotalMoment / sumTotalWeight) : null;

  const aircraftPoint = {
    kind: "aircraft_point",
    Weight: sumTotalWeight,
    cgIn: sumTotalCg,
  };
  
  console.log("WbalTable: SUM TOTAL WEIGHT:", sumTotalWeight);
  console.log("WbalTable: SUM TOTAL MOMENT:", sumTotalMoment);
  console.log("WbalTable: SUM TOTAL CG:", sumTotalCg);

  // Build display rows in final order:
  // normal stations, then fuel rows, then final sum row
  /**
   * for the array:  [{ id: "Pilot", Weight: 200, Arm: 40.25 }, { id: "Baggage", Weight: 50, Arm: 93 }]
   * .map() transforms each element 'r' into a new object 
   * that includes a 'kind' property set to "normal" and 
   * spreads the original properties of 'r'
   * arrow function: => ({})  returns a new object for each element in the array
   * arrow function with only this {} causes javascript to interpret it as 
   * a function body and not an object literal.
   */

  console.log("initialFuelGal =", initialFuelGal);
  console.log("runupFuelGal =", runupFuelGal);
  console.log("fuelArm =", fuelArm);
  console.log("lbsPerGal =", lbsPerGal);

  const displayRows = [
    ...normalRows.map((r) => ({ kind: "normal", ...r })),

    {
      kind: "fuel_initial",
      id: "Initial Fuel",
      galLabel: "Initial Fuel (gal)",
      galValue: initialFuelGal,
      Weight: initialFuelLb,
      Arm: fuelArmNum,
      momentLbIn: initialFuelMoment,
    },
    {
      kind: "fuel_runup",
      id: "Runup Fuel",
      galLabel: "Runup Fuel (gal)",
      galValue: runupGal != null ? runupGal.toFixed(2) : "",
      Weight: runupFuelLb != null ? -runupFuelLb : null,
      Arm: fuelArmNum,
      momentLbIn: runupFuelMoment != null ? -runupFuelMoment : null,
    },
    {
      kind: "fuel_takeoff",
      id: "Takeoff Fuel",
      galLabel: "Takeoff Total Fuel (gal)",
      galValue: takeoffGal != null ? takeoffGal.toFixed(2) : "",
      Weight: takeoffFuelLb,
      Arm: fuelArmNum,
      momentLbIn: takeoffFuelMoment,
    },
    {
      kind: "fuel_unusable",
      id: "Unusable Fuel",
      galLabel: "Unusable Fuel (gal)",
      galValue: unusableGalNum != null ? unusableGalNum.toFixed(2) : "",
      Weight: "",
      Arm: "",
      momentLbIn: "",
    },
    {
      kind: "fuel_takeoff_usable",
      id: "Takeoff Usable Fuel",
      galLabel: "Takeoff Usable Fuel (gal)",
      galValue: takeoffUsableGal != null ? takeoffUsableGal.toFixed(2) : "",
      Weight: "",
      Arm: "",
      momentLbIn: "",
    },

    {
      kind: "sum_total",
      id: "Sum Total",
      Weight: sumTotalWeight,
      Arm: "", // Center of Gravity for sum total row
      momentLbIn: sumTotalMoment,
      cgIn: "",
    },
    {
        kind: "max_gross_info",
        id: "Max Allowed Gross Weight",
        Weight: maxGrossLbs,
        Arm: "",
        momentLbIn: "",
        cgIn: "",
      },
      {
        kind: "cg_info",
        id: "Takeoff CG",
        Weight: "",
        Arm: "",
        momentLbIn: "",
        cgIn: sumTotalCg,
      },
      
  ];

  console.log("WbalTable: CURRENT wbal STATE:", wbal);
  console.log("WbalTable >>> envelope being passed to graph:", envelope);

  return (
    <>
      <table id="wtbals">
        <caption className='caption_text'>Weight and Balance Table (from Firebase)</caption>
        <thead>
          <tr>
            <th className='station_box'>Station</th>
            <th className='weight_box'>Weight lbs.</th>
            <th className='arm_box'>Arm In.</th>
            <th className='moment_box'>Moment lb-In.</th>
            {canEdit && <th className='edit_box'>Edit</th>}
            {canEdit && <th className='delete_box'>Delete</th>}
          </tr>
        </thead>
        {/* https://legacy.reactjs.org/docs/lists-and-keys.html */}
        <tbody>
          {displayRows.map((row, i) => (
            <WtBalRow
              key={`${row.kind}-${row.id}-${i}`}
              wbal={row}
              onEdit={onEdit}
              onDelete={handleDelete}
              initialFuelGal={initialFuelGal}
              setInitialFuelGal={setInitialFuelGal}
              runupFuelGal={runupFuelGal}
              setRunupFuelGal={setRunupFuelGal}
              fuelArm ={fuelArm}
              setFuelArm={setFuelArm}
              canEdit={canEdit}
            />
          ))}
        </tbody>
      </table>
      {canEdit && (
      <div className="wtbal-controls">
          <button type="button"
          onClick={() => navigate(`/add-wtbal?tail=${encodeURIComponent(tail)}`)}
          style={{
            marginTop: "5px",
            padding: "8px 14px",
            fontWeight: "600",
            cursor: "pointer"
          }}
      >
        + Create / Edit Stations
      </button>
      
      <br></br> {/* spacing */}

      
      <button type="button"
      onClick={() => navigate(`/edit-envelope?tail=${encodeURIComponent(tail)}`)}
      style={{
        marginTop: "5px",
        padding: "8px 14px",
        fontWeight: "600",
        cursor: "pointer"
      }}>
        + Edit Envelope
      </button>
      </div>
      )}
      <br /> {/* spacing */}

      <h4 className='caption_text'>Weight and Balance Envelope</h4>
      <p> Lbs. vs. CG (inches aft of datum)</p>
      {/* graph should use takeoff fuel + normal stations */}
      <WtBalGraph data={[aircraftPoint]} envelope={envelope}/>
     </>
  );
}
  export default WtBalTable;